import { useState } from 'react';
import type { InvoiceStatus } from './useStripeInvoices';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    recipient: string;
    amount: number;
    description: string;
    notes: string;
    due_date: string | null;
    status: InvoiceStatus;
  }) => Promise<void>;
}

export default function InvoiceCreateModal({ open, onClose, onCreate }: Props) {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  if (!open) return null;

  function resetForm() {
    setRecipient('');
    setAmount('');
    setDescription('');
    setDueDate('');
    setNotes('');
    setError('');
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  async function handleSubmit(status: InvoiceStatus) {
    if (!recipient || !amount) return;
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onCreate({
        recipient,
        amount: parsedAmount,
        description,
        notes,
        due_date: dueDate || null,
        status,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="rounded-lg w-full max-w-md mx-4 p-6 shadow-2xl"
        style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            className="font-sans text-base font-medium"
            style={{ color: 'var(--dash-text-primary)' }}
          >
            New Stripe Invoice
          </h2>
          <button
            onClick={handleClose}
            className="transition-colors text-lg leading-none"
            style={{ color: hoveredBtn === 'close' ? 'var(--dash-text-tertiary)' : 'var(--dash-text-dim)' }}
            onMouseEnter={() => setHoveredBtn('close')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            x
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              className="block font-mono text-[10px] uppercase tracking-widest mb-1.5"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Recipient Email
            </label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="customer@example.com"
              className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-text-secondary)',
              }}
            />
          </div>
          <div>
            <label
              className="block font-mono text-[10px] uppercase tracking-widest mb-1.5"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Amount (USD)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 rounded font-mono text-sm focus:outline-none transition-colors"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-text-secondary)',
              }}
            />
          </div>
          <div>
            <label
              className="block font-mono text-[10px] uppercase tracking-widest mb-1.5"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Services rendered, product order, etc."
              className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-text-secondary)',
              }}
            />
          </div>
          <div>
            <label
              className="block font-mono text-[10px] uppercase tracking-widest mb-1.5"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-select-text)',
              }}
            />
          </div>
          <div>
            <label
              className="block font-mono text-[10px] uppercase tracking-widest mb-1.5"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Notes (internal)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes, not visible to recipient..."
              rows={2}
              className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors resize-none"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-text-secondary)',
              }}
            />
          </div>

          {error && (
            <div className="border border-red-500/30 rounded bg-red-500/5 p-3">
              <p className="text-red-400 text-xs font-sans">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-5">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded font-sans text-sm transition-colors"
            style={{
              border: '1px solid var(--dash-border)',
              color: hoveredBtn === 'cancel' ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
            }}
            onMouseEnter={() => setHoveredBtn('cancel')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit('draft')}
            disabled={!recipient || !amount || saving}
            className="px-4 py-2 rounded font-sans text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              border: '1px solid var(--dash-border)',
              color: hoveredBtn === 'draft' ? 'var(--dash-text-primary)' : 'var(--dash-text-tertiary)',
            }}
            onMouseEnter={() => setHoveredBtn('draft')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSubmit('sent')}
            disabled={!recipient || !amount || saving}
            className="px-4 py-2 rounded bg-white text-zinc-900 font-sans text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
